import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "cn";
import { BadgeDot } from "@/components/ui/badge/badge-dot";
import { Button } from "@/components/ui/button/button";
import { BwFilterLine } from "@/components/ui/icons";

import { useActiveFilterToggle } from "../_hooks/use-active-filter-toggle";
import { useFilters } from "../_hooks/use-filters";
import type { FilterFieldsConfig } from "../types";
import { FilterMenu } from "./filter-menu";

/**
 * Funnel button sizing + badge positioning. Ported verbatim from the legacy
 * `shared/filters/_components/filter-toggle`. The
 * `[&_svg:not([class*='size-'])]:size-*` rule sizes the icon via CSS — a numeric
 * `size` prop on the icon is overridden by the Button base's `[&_svg…]:size-5`,
 * so the class is what actually applies.
 */
export const buttonVariants = cva(
  "relative group transition text-xs shrink-0 [&_svg]:shrink-0",
  {
    variants: {
      size: {
        "icon-xs":
          "h-6 w-6 [&_svg:not([class*='size-'])]:size-4 **:data-[slot=badge]:top-1 **:data-[slot=badge]:right-1 **:data-[slot=badge]:size-1",
        "icon-sm":
          "h-7 w-7 [&_svg:not([class*='size-'])]:size-4 **:data-[slot=badge]:top-1.5 **:data-[slot=badge]:right-1.5 **:data-[slot=badge]:size-1",
        "icon-md":
          "h-8 w-8 [&_svg:not([class*='size-'])]:size-5 **:data-[slot=badge]:top-1.5 **:data-[slot=badge]:right-1.5 **:data-[slot=badge]:size-1.5",
      },
    },
    defaultVariants: {
      size: "icon-md",
    },
  },
);

type FilterToggleProps = VariantProps<typeof buttonVariants> & {
  fields: FilterFieldsConfig;
  className?: string;
};

/**
 * Header filter entry point. When any configured field is active, shows the
 * funnel with a rose badge that toggles the `<ActiveFilterBar>` open/closed
 * (via the shared `useActiveFilterToggle` store the bar already respects). When
 * nothing is active, shows the "+ Add filter" menu instead. Keys outside
 * `fields` (e.g. a `status` tab) never count as active, so they don't trip the
 * badge. Replaces the per-feature `FilterToggle` + `ActiveFilterButton` pair.
 */
export const FilterToggle = ({
  fields,
  size,
  className,
}: FilterToggleProps) => {
  const { filters } = useFilters();
  const { open, actions } = useActiveFilterToggle();

  const filterValues = filters as Record<string, unknown>;
  const hasActive = fields.some(
    (field) => filterValues[field.key] !== undefined,
  );

  if (!hasActive) return <FilterMenu fields={fields} size={size} />;

  // An icon-only button that shows and hides the applied filters: it needs a name, and
  // `aria-expanded` says whether the bar is showing.
  return (
    <Button
      kind="ghost"
      variant="neutral"
      color="black"
      size={size}
      aria-label="Filters"
      aria-expanded={open}
      className={cn(buttonVariants({ size }), className)}
      onClick={() => actions.toggleOpen()}
    >
      <BwFilterLine />
      <BadgeDot data-slot="badge" className="absolute" color="rose" />
    </Button>
  );
};
